import type {
  DatasetColumn,
  DatasetManifestEntry,
  ReportDefinition,
} from '../types/reporting';
import { splitRef, type RelatedManifests } from './columnRefs';
import { resultColumnKeys } from './definitionUpdates';

/** Each column by its ref: `name` for the primary, `key.name` for a related dataset. */
export function columnMetaByRef(
  dataset: DatasetManifestEntry | null,
  related: RelatedManifests
): Map<string, DatasetColumn> {
  const meta = new Map<string, DatasetColumn>();
  dataset?.columns.forEach(c => meta.set(c.name, c));
  related.forEach((entry, key) => {
    entry.columns.forEach(c => meta.set(`${key}.${c.name}`, c));
  });
  return meta;
}

/**
 * The refs whose labels can show in a header: the result columns plus each
 * measure's base column ("Sum of Balance" reads the label of `balance`). Pass
 * this as `selected` to defaultColumnLabels so two measure bases with the same
 * label keep their dataset.
 */
export function columnLabelRefs(
  def: ReportDefinition,
  dataset: DatasetManifestEntry
): string[] {
  const bases = def.measures.flatMap(m => (m.column ? [m.column] : []));
  return [...new Set([...resultColumnKeys(def, dataset), ...bases])];
}

/**
 * Default header for every known column ref: the column's own label. When two of the
 * `selected` refs share a label, each of those becomes "Label (Dataset)".
 * Unselected refs keep the plain label. A ref listed twice counts once.
 */
export function defaultColumnLabels(
  dataset: DatasetManifestEntry,
  related: RelatedManifests,
  selected: string[]
): Map<string, string> {
  const labels = new Map<string, string>();
  dataset.columns.forEach(c => labels.set(c.name, c.label));
  related.forEach((entry, key) => {
    entry.columns.forEach(c => labels.set(`${key}.${c.name}`, c.label));
  });

  const refs = [...new Set(selected)];
  const counts = new Map<string, number>();
  refs.forEach(ref => {
    const label = labels.get(ref);
    if (label !== undefined) counts.set(label, (counts.get(label) ?? 0) + 1);
  });
  refs.forEach(ref => {
    const label = labels.get(ref);
    if (label === undefined || (counts.get(label) ?? 0) < 2) return;
    const { dataset: key } = splitRef(ref);
    const datasetLabel =
      key === null ? dataset.label : (related.get(key)?.label ?? key);
    labels.set(ref, `${label} (${datasetLabel})`);
  });
  return labels;
}

/**
 * Custom names (definition.column_labels) over the defaults, only for keys in
 * `resultKeys`. A stale key is ignored so it can never rename a measure base.
 */
export function mergeColumnLabels(
  defaults: Map<string, string>,
  custom: Record<string, string> | undefined,
  resultKeys: string[]
): Map<string, string> {
  const merged = new Map(defaults);
  if (!custom) return merged;
  resultKeys.forEach(key => {
    const name = custom[key];
    if (name) merged.set(key, name);
  });
  return merged;
}
